import { isEmpty, truncate } from "lodash-es";
import type { EmailEditorProps } from "react-email-editor";
import { useFlags } from "../../../server/optimizely";
import { useSettings } from "../../../utils/hooks/use-settings";
import { notEmpty } from "../../../utils/not-empty";
import { serviceURL } from "../../../utils/service-url";
import { useAccountLimitsContext } from "../../context/account-limit-context";
import { useAccountId } from "../../generic/hooks/use-account-id";
import type { UnlayerMergeTags } from "../../merge-tag/util";
import { makeSurveyUrl } from "../../message/editor/components/utils/survey-url";
import { makePollCustomToolSource } from "../custom-tools/poll-custom-tool";
import { makeSurveyAnswersCustomTool } from "../custom-tools/survey-answers-custom-tool";
import { useUnlayerAccount } from "./use-unlayer-account";
import { useUnlayerSurveys } from "./use-unlayer-surveys";

export function useUnlayerEditorOptions(
  mergeTags: UnlayerMergeTags,
  isSurveyTrigger: boolean,
):
  | { loading: true; data: null }
  | { loading: false; data: NonNullable<EmailEditorProps["options"]> } {
  const { settings } = useSettings();
  const accountId = useAccountId();
  const { enable_polls: accountPollsEnabled } = useAccountLimitsContext();
  const unlayerAccount = useUnlayerAccount();
  const {
    flags: { "cha-4288-polls-in-newsletters": pollsInNewsletters },
    flagsLoaded,
  } = useFlags("cha-4288-polls-in-newsletters");
  const pollsEnabled = pollsInNewsletters && accountPollsEnabled;

  const { data: surveys, loading: surveysLoading } = useUnlayerSurveys();

  if (!unlayerAccount || surveysLoading || !accountId || !flagsLoaded) {
    return { loading: true, data: null };
  }

  return {
    loading: false,
    data: {
      id: "editor-email",
      appearance: { theme: settings.theme },
      projectId: unlayerAccount.projectId as unknown as number,
      user: {
        id: unlayerAccount.userId,
        signature: unlayerAccount.signature,
      },
      features: {
        userUploads: { enabled: true, search: true },
        textEditor: { tables: true },
        preview: false,
      },
      tools: {
        social: { enabled: true },
        table: { enabled: true },
      },
      mergeTags,
      mergeTagsConfig: { sort: false },
      displayMode: "email",
      linkTypes: surveys ? [makeSurveyLinkType(accountId, surveys)].filter(notEmpty) : undefined,
      translations: {
        "en-US": {
          "tools.tabs.uploads": "Library",
        },
      },
      tabs: {
        userUploads: {
          icon: "fa-folder-image",
        },
      },
      // Hides the delete & upload buttons in the custom file library
      customCSS: [
        `.gallery .item-context-menu-button,
            .blockbuilder-uploads-panel header,
            .blockbuilder-uploads-panel .blockbuilder-dropzone-container
               { display: none !important; }`,
        `div.react-select__option { height: 70px !important; white-space: normal !important; word-break: break-word !important; }`,
      ],
      customJS:
        pollsEnabled || isSurveyTrigger
          ? [
              ...(pollsEnabled
                ? [
                    makePollCustomToolSource(window.location.origin, accountId, {
                      pollResponseUrlPrefix: `${serviceURL("poll-position")}poll_response/${accountId}/`,
                    }),
                  ]
                : []),
              ...(isSurveyTrigger ? [makeSurveyAnswersCustomTool()] : []),
            ]
          : undefined,
    },
  };
}

function makeSurveyLinkType(
  accountId: string,
  surveys: { id: string; title: string }[],
): NonNullable<NonNullable<EmailEditorProps["options"]>["linkTypes"]>[0] | null {
  return {
    name: "survey",
    label: "Survey",
    attrs: {
      href: "{{survey}}",
      target: "_blank",
    },
    fields: [
      {
        name: "survey",
        label: "Survey",
        inputType: null,
        options: (surveys ?? []).map((survey) => ({
          value: makeSurveyUrl(accountId, survey.id, { anonymous: false }),
          label: truncate(isEmpty(survey.title) ? "Untitled Survey" : survey.title, { length: 35 }),
        })),
        sortOptions: true,
      },
    ],
  };
}
